import { useAppSelector, useAppDispatch } from '../../state/store';

import { formatJapaneseDate } from '../../utils/date';
import { clearJournal, loadJournalEntries, removeEntry } from '../../state/features/app';
import { JournalEntry } from '@common/models/journal';
import { useEffect, useState } from 'react';
import { deleteEntry } from '../../clients/api';

import styles from './styles.module.scss';

interface JournalEntriesProps {}

interface GroupedEntry {
    day: string;
    entries: JournalEntry[];
}

function sortByCreatedAt(entries: JournalEntry[], reverse: boolean = false): JournalEntry[] {
    const entriesWithDate = entries.filter((entry) => entry.created_at);
    if (reverse) {
        return entriesWithDate.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    }
    return entriesWithDate.sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
}

function groupEntriesByDay(entries: JournalEntry[]): GroupedEntry[] {
    if (!Array.isArray(entries)) {
        return [];
    }
    const grouped = entries.reduce((groups, entry) => {
        const day = entry.created_at.split('T')[0]; // Extract day from date
        if (!groups[day]) {
            groups[day] = [];
        }
        groups[day].push(entry);
        return groups;
    }, {} as Record<string, JournalEntry[]>);

    return Object.entries(grouped)
        .map(([day, entries]) => ({
            day,
            entries: sortByCreatedAt(entries, true), // Sort entries in reverse chronological order
        }))
        .sort((a, b) => b.day.localeCompare(a.day)); // Sort groups in reverse chronological order
}

export default function JournalEntries({}: JournalEntriesProps) {
    const dispatch = useAppDispatch();
    const { journalEntries } = useAppSelector((state) => state.app);
    console.log('Journal Entries:', journalEntries);

    const [viewExpanded, setViewExpanded] = useState(false);

    const handleClearJournal = () => {
        dispatch(clearJournal());
    };

    window.clearJournal = handleClearJournal;

    const handleRemoveEntry = async (id: string) => {
        try {
            await deleteEntry(id);
            dispatch(removeEntry(id));
        } catch (error) {
            console.error('Failed to delete entry:', error);
        }
    };

    function renderFeedback(feedback: string) {
        if (!feedback) {
            return null;
        }
        let processedFeedback = '';
        if (feedback.trim() === 'Correct!') {
            processedFeedback = '完璧です！👍';
        } else {
            processedFeedback = feedback.trim();
        }

        return <div className={styles.feedback}>{processedFeedback}</div>;
    }

    function renderExpandedDayView(entries: JournalEntry[]) {
        return entries.map((entry, index) => {
            const correctionPrompt = encodeURIComponent(
                'You are a native Japanese speaker and professional Japanese teacher. You focus on both correct grammar and natural sounding structures. Correct following sentence. \n\n' +
                    entry.content.entry +
                    '\n\nRespond in english. Use Kanji but add furigana in brackets where required. Translate the meaning of the sentence to english as well.',
            );

            return (
                <div key={index} className={styles.entry}>
                    <div className={styles.journalEntry}>{entry.content.entry}</div>
                    {renderFeedback(entry.content.feedback)}
                    <div>
                        <button onClick={() => handleRemoveEntry(entry.id)}>Remove Entry</button>
                        <a
                            className={styles.extLink}
                            href={`https://www.chatgpt.com/?q=${correctionPrompt}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            ChatGPT Correction
                        </a>
                        <a
                            className={styles.extLink}
                            href={`https://claude.ai/new?q=/${correctionPrompt}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Claude Correction
                        </a>
                    </div>
                </div>
            );
        });
    }

    function renderCollapsedDayView(entries: JournalEntry[]) {
        return (
            <div className={styles.journalEntry}>
                {sortByCreatedAt(entries)
                    .map((entry) => entry.content.entry)
                    .join('')}
            </div>
        );
    }

    const groupedEntries = groupEntriesByDay(journalEntries);

    useEffect(() => {
        dispatch(loadJournalEntries());
    }, []);

    return (
        <div className={styles.JournalEntries}>
            {groupedEntries.map(({ day, entries }, i) => (
                <div key={day} className={styles.day}>
                    <div className={styles.dayEntries}>
                        {viewExpanded ? renderExpandedDayView(entries) : renderCollapsedDayView(entries)}
                    </div>
                    <div className={styles.createdAt}>
                        {formatJapaneseDate(day)}
                        <div className={styles.actions}>
                            <div
                                onClick={() => {
                                    setViewExpanded(!viewExpanded);
                                }}
                                className={styles.btn}
                            >
                                {viewExpanded ? '-' : '+'}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
